import { useEffect, useMemo, useRef, useState } from 'react';

import useWindowSize from 'hooks/use-window-size';
import { useAppSelector } from 'store/store-hooks';
import { CLIP_ASPECT_RATIO, MIN_CLIP_WIDTH } from 'constants/params';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { countStoryboardClips, selectClipData } from 'components/app/_helpers/_app.helper';
import { useMemorySr } from 'components/app/_mediator/memory/use-memory-sr';
import { useClipThumbPrevContext } from 'components/app/_mediator/clip-thumb-prev.provider';
import { useMemory } from 'components/app/_mediator/memory/use-memory';
import { selectSelectedFile } from 'components/app/_mediator/app-files.slice';
import { selectFilteredFileClips } from 'components/app/editor/_mediator/editor.slice';
import { useSelector } from 'components/app/selector/_mediator/use-selector';

import { ClipThumbPrev } from 'components/app/app-components/clip-thumb-prev/clip-thumb-prev';
import { SelectorClip } from './selector-clip';
import { __SrEmpty } from './_sr-empty';
import styles from './_selector-clips.module.scss';

const VAR__GRID_COLUMNS = '--grid-columns';

export const SelectorClipsGrid = () => {
  const { resetHoveredClip } = useClipThumbPrevContext();
  const { width: screenWidth, height: screenHeight } = useWindowSize();
  const { activeClip, clipsMap } = useAppSelector((state) => state.editor);
  const selector = useAppSelector((state) => state.editor.selector);
  const snapshot = useAppSelector((state) => state.memory.selector);
  const clips = useAppSelector(selectFilteredFileClips);
  const selectedFile = useAppSelector(selectSelectedFile);
  const [init, setInit] = useState(false);
  const [renderClips, setRenderClips] = useState<string[]>([]);
  const [showClipTooltip, setShowClipTooltip] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const clipRef = useRef<HTMLDivElement>(null);

  useSelector();
  useMemory(snapshot, useMemorySr());

  // Calculate storyboard count (pinned highlights + manual clips)
  const storyboardCount = useMemo(() => {
    if (!selectedFile || !selector) return 0;
    const fileClips = selector[selectedFile.id];
    return countStoryboardClips(fileClips, clipsMap);
  }, [selectedFile, selector, clipsMap]);

  useEffect(() => {
    if (clipsMap && clips && selectedFile) setInit(true);
  }, [clipsMap, clips, selectedFile]);

  useEffect(() => {
    if (!init) return;
    const bodyNode = gridRef.current;
    const clipNode = clipRef.current;
    if (!clipNode || !bodyNode) return;
    const { top: clipTop, bottom: clipBottom } = clipNode.getBoundingClientRect();
    const { bottom: bodyBottom, top: bodyTop } = bodyNode.getBoundingClientRect();
    const scrollTop = bodyNode.scrollTop;

    const bottom = bodyBottom - clipBottom;
    const top = clipTop - bodyTop;

    if (bottom < 0) {
      bodyNode.scroll({ top: scrollTop - bottom, behavior: 'smooth' });
    } else if (top < 0) bodyNode.scroll({ top: scrollTop + top, behavior: 'smooth' });
  }, [init, activeClip, renderClips]);

  useEffect(() => {
    if (!clips) return;

    const trackFeedWidth = gridRef.current?.offsetWidth;
    const trackFeedHeight = gridRef.current?.offsetHeight;

    let layoutReady = false;

    if (trackFeedWidth && trackFeedHeight) {
      const maxColumns = Math.floor(trackFeedWidth / MIN_CLIP_WIDTH);
      let columns = maxColumns;

      while (!layoutReady) {
        const clipsWidth = (trackFeedWidth - (columns - 1) * 8) / columns;
        const clipsHeight = CLIP_ASPECT_RATIO * clipsWidth;
        const rows = Math.ceil(clips.length / columns);
        const emptyCells = columns * rows - clips.length;
        const spaceAtTheBottom = trackFeedHeight - clipsHeight * rows - (rows - 1) * 8;

        if (spaceAtTheBottom > clipsHeight || (emptyCells && spaceAtTheBottom > 0)) {
          columns = columns - 1;
        } else {
          layoutReady = true;
          // We need this to prevent feed blinking while clips are structuring
          setRenderClips(clips);

          let additionalColumns = 0;
          if (spaceAtTheBottom <= 0) additionalColumns = 1;

          gridRef.current.style.setProperty(VAR__GRID_COLUMNS, `${columns + additionalColumns}`);
          const clipWidth = (trackFeedWidth - columns * 8) / (columns + additionalColumns);
          setShowClipTooltip(clipWidth < MIN_CLIP_WIDTH + 50);
          break;
        }
      }
    }
  }, [clips, screenWidth, screenHeight]);

  if (!clipsMap || !clips || !selectedFile) return null;

  return (
    <div className={styles.Grid} onMouseLeave={resetHoveredClip}>
      <div className={styles.Grid__header}>
        {selectedFile.fileName} (Highlights: {storyboardCount} (storyboarded) / {clips.length} (available))
      </div>
      <div className={styles.Grid__body}>
        <div ref={gridRef} className={styles.Grid__container} data-intro={IntroKeys.srGrid}>
          {renderClips.length === 0 && <__SrEmpty />}
          {renderClips.map((clipId) => (
            <SelectorClip
              ref={clipRef}
              clipData={selectClipData(clipId, clipsMap)}
              key={clipId}
              showClipTooltip={showClipTooltip}
            />
          ))}
        </div>
      </div>
      {showClipTooltip && <ClipThumbPrev />}
    </div>
  );
};
